import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import jwt from "jwt-decode";
import user_service from "../service/user_service";
import { vendorAgreementSignatureOnFile } from "../Contact/VendorOnboarding/constants";
import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster.js";

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString();
}

/**
 * Maps listing + contact to card headline, accent, and actions (single source of truth).
 */ 
function getListingCardState(row, vendorContact, contactId) {
  const id = row._id ? String(row._id) : "";
  const catId = row.category_id ? String(row.category_id) : "";
  const editTo = catId ? `/update-markting/${catId}/listing/${id}` : null;
  const viewTo = `/promote/catalog-details/promo/${id}`;

  const adStatus = String(row.vendorAdStatus || "").toLowerCase();
  const payStatus = String(row.vendorAdPaymentStatus || "").toLowerCase();
  const editStatus = String(row.vendorAdEditStatus || "").toLowerCase();
  const editPending = editStatus === "pending";
  const adPending = adStatus === "pending";
  const paid = payStatus === "paid";
  const paymentReview = payStatus === "payment_review";
  const paymentRejected = payStatus === "payment_rejected";
  const hasAgreement = vendorAgreementSignatureOnFile(vendorContact);
  const resumeTo =
    adPending && contactId ? `/vendor-new-ad/${contactId}?resume=${id}` : null;
  const needsVendorAction =
    adPending &&
    contactId &&
    (paymentRejected ||
      (!paid && !paymentReview) ||
      ((paid || paymentReview) && !hasAgreement));

  let headline = "";
  let subtext = "";
  /** Left accent on “What’s next” panel (Bootstrap border-*). */
  let accent = "secondary";
  let resumeLabel = "";

  if (!adPending) {
    if (adStatus === "approved") {
      if (editPending) {
        headline = "Changes submitted for review";
        subtext =
          "Your approved ad is still live, but your latest edits are waiting for admin approval.";
        accent = "primary";
      } else {
        headline = "Live on the platform";
        subtext = "Your ad is approved and visible to users.";
        accent = "success";
      }
    } else if (adStatus === "rejected") {
      if (payStatus === "refund_pending") {
        headline = "Not approved — refund in progress";
        subtext =
          "Your payment is being refunded back to your card. This can take a few business days depending on your bank.";
        accent = "warning";
      } else if (payStatus === "refund_failed") {
        headline = "Not approved — refund delayed";
        subtext =
          "We’re retrying your refund. If it doesn’t update soon, contact support.";
        accent = "warning";
      } else if (payStatus === "refunded") {
        headline = "Not approved — refunded";
        subtext =
          "Your payment has been refunded. You can create a new ad when ready.";
        accent = "secondary";
      } else {
        headline = "Not approved";
        subtext = "Reach out to support if you’d like to discuss next steps.";
        accent = "danger";
      }
    } else if (adStatus === "expired") {
      headline = "Expired";
      subtext = "This ad term has ended. Create a new ad if you’d like to return.";
      accent = "secondary";
    } else {
      headline = row.vendorAdStatus || "—";
      subtext = "";
      accent = "secondary";
    }
  } else if (paymentRejected) {
    headline = "Payment proof not accepted";
    subtext =
      "Please upload updated payment proof or pay with card to continue.";
    accent = "warning";
    resumeLabel = "Resubmit payment";
  } else if (paymentReview) {
    headline = "Payment under review";
    subtext =
      "Our team is verifying your payment. You can continue signing the agreement while you wait.";
    accent = "primary";
    if (!hasAgreement) {
      resumeLabel = "Continue to sign";
    }
  } else if (!paid) {
    headline = "Payment required";
    subtext =
      "Complete checkout or submit payment by another method for our team to review.";
    accent = "warning";
    resumeLabel = "Pay now";
  } else if (!hasAgreement) {
    headline = "Agreement & signature";
    subtext =
      "Sign the vendor agreement once in the wizard so we can process your ad.";
    accent = "warning";
    resumeLabel = "Continue to sign";
  } else {
    headline = "Awaiting team review";
    subtext =
      "You’re all set on your side. We’ll notify you when your ad is approved.";
    accent = "primary";
  }

  return {
    id,
    editTo,
    viewTo,
    headline,
    subtext,
    accent,
    needsVendorAction,
    resumeTo,
    resumeLabel,
    adPending,
    paid,
    payStatus,
    adStatus,
    editStatus,
    editPending,
    vendorAdStatus: row.vendorAdStatus,
    vendorAdPaymentStatus: row.vendorAdPaymentStatus,
    expiresLabel: formatDate(row.vendorAdExpiresAt),
  };
}

const ACCENT_BORDER = {
  success: "border-success",
  warning: "border-warning",
  danger: "border-danger",
  primary: "border-primary",
  secondary: "border-secondary",
};

/**
 * Logged-in vendor: list own ads, status, and shortcuts to view / edit / create.
 */
const VendorMyAds = () => {
  const [loader, setLoader] = useState({ isActive: null });
  const { isActive } = loader;
  const [toaster, setToaster] = useState({
    types: null,
    isShow: null,
    toasterBody: "",
    message: "",
  });
  const { types, isShow, toasterBody, message } = toaster;

  const [items, setItems] = useState([]);
  const [vendorContact, setVendorContact] = useState(null);
  const decoded = useMemo(() => {
    try {
      const t = localStorage.getItem("auth");
      return t ? jwt(t) : null;
    } catch {
      return null;
    }
  }, []);

  const isVendor =
    decoded && String(decoded.contactType || "").toLowerCase() === "vendor";
  const contactId = decoded?.id ? String(decoded.id) : "";

  const load = async () => {
    setLoader({ isActive: true });
    try {
      const resp = await user_service.vendorAdsMyListings();
      const data = resp?.data?.data || [];
      setItems(Array.isArray(data) ? data : []);
      setLoader({ isActive: false });
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "",
        message:
          e?.response?.data?.message ||
          e?.message ||
          "Could not load your listings.",
      });
      setTimeout(() => setToaster((x) => ({ ...x, isShow: false })), 3500);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!isVendor || !contactId) return;
    load();
    (async () => {
      try {
        const r = await user_service.contactGetById(contactId);
        setVendorContact(r?.data || null);
      } catch {
        setVendorContact(null);
      }
    })();
  }, [isVendor, contactId]);

  if (!isVendor) {
    return (
      <div className="bg-secondary float-left w-100 pt-4 mb-4">
        <main className="page-wrapper profile_page_wrap">
          <div className="content-overlay">
            <div className="container">
              <div className="alert alert-warning mt-4">
                This page is only available to vendor accounts.
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-secondary float-left w-100 pt-4 pb-5 mb-4">
      <Loader isActive={isActive} />
      {isShow && (
        <Toaster
          types={types}
          isShow={isShow}
          toasterBody={toasterBody}
          message={message}
        />
      )}
      <main className="page-wrapper profile_page_wrap">
        <div className="content-overlay">
          <div className="container">
            <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
              <div className="flex-grow-1" style={{ maxWidth: "42rem" }}>
                <h3 className="text-dark mb-2">My vendor ads</h3>
                <p className="text-muted small mb-0 lh-base">
                  Each card shows where your listing stands. Use{" "}
                  <strong className="text-body">Pay now</strong> or{" "}
                  <strong className="text-body">Continue to sign</strong> only
                  when we still need something from you. Otherwise you can{" "}
                  <strong className="text-body">View</strong> or{" "}
                  <strong className="text-body">Edit</strong> details anytime.
                </p>
              </div>
              <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => load()}
                >
                  Refresh
                </button>
                <NavLink
                  className="btn btn-primary btn-sm"
                  to={
                    contactId
                      ? `/vendor-new-ad/${contactId}?draft=new`
                      : "/"
                  }
                >
                  Create new ad
                </NavLink>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5 text-muted">
                  You do not have any vendor listings yet.{" "}
                  <NavLink to={`/vendor-onboarding/${contactId}`}>
                    Start vendor onboarding
                  </NavLink>{" "}
                  to create your first ad.
                </div>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {items.map((row) => {
                  const s = getListingCardState(row, vendorContact, contactId);
                  return (
                    <div
                      key={s.id || Math.random()}
                      className="card border-0 shadow-sm overflow-hidden"
                    >
                      <div className="card-body p-0">
                        <div className="p-3 p-md-4">
                          <div className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-start">
                            <div className="min-w-0 flex-grow-1">
                              <h5 className="h5 mb-1 text-truncate">
                                {row.productname || "Untitled listing"}
                              </h5>
                              <p className="small text-muted mb-0">
                                {row.companyName ? (
                                  <span>{row.companyName}</span>
                                ) : (
                                  <span className="fst-italic">
                                    No company name
                                  </span>
                                )}
                              </p>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                <span className="badge rounded-pill bg-light text-dark border">
                                  Listing:{" "}
                                  <span className="fw-semibold">
                                    {s.vendorAdStatus || "—"}
                                  </span>
                                </span>
                                <span
                                  className={`badge rounded-pill ${
                                    s.paid
                                      ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25"
                                      : "bg-warning bg-opacity-25 text-dark border border-warning"
                                  }`}
                                >
                                  Payment:{" "}
                                  <span className="fw-semibold text-capitalize">
                                    {s.vendorAdPaymentStatus || "—"}
                                  </span>
                                </span>
                                {s.expiresLabel ? (
                                  <span className="badge rounded-pill bg-light text-muted border">
                                    Expires {s.expiresLabel}
                                  </span>
                                ) : (
                                  <span className="badge rounded-pill bg-light text-muted border">
                                    No expiry date yet
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className="btn-group btn-group-sm flex-shrink-0"
                              role="group"
                              aria-label="Listing actions"
                            >
                              <NavLink
                                className="btn btn-outline-primary"
                                to={s.viewTo}
                              >
                                View
                              </NavLink>
                              {s.editTo ? (
                                <NavLink
                                  className="btn btn-primary"
                                  to={s.editTo}
                                >
                                  Edit
                                </NavLink>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  disabled
                                  title="Category is missing — edit unavailable"
                                >
                                  Edit
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <div
                          className={`px-3 px-md-4 pb-3 pb-md-4 pt-0 border-top border-light bg-light bg-opacity-50`}
                        >
                          <div
                            className={`rounded-3 bg-white border border-start border-4 ${
                              ACCENT_BORDER[s.accent] || ACCENT_BORDER.secondary
                            } p-3 shadow-sm`}
                          >
                            <p className="fw-semibold text-body mb-1 small text-uppercase letter-spacing-wide text-muted">
                              What&apos;s next
                            </p>
                            <h6 className="h6 mb-2">{s.headline}</h6>
                            {s.subtext ? (
                              <p className="small text-muted mb-3 mb-md-0">
                                {s.subtext}
                              </p>
                            ) : null}
                            {s.needsVendorAction && s.resumeTo ? (
                              <NavLink
                                className="btn btn-primary btn-sm w-100 w-md-auto mt-2"
                                to={s.resumeTo}
                              >
                                {s.resumeLabel}
                              </NavLink>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default VendorMyAds;
